import { useEffect, useRef, useState } from "react";
import QRCodeStyling, {
  DrawType,
  TypeNumber,
  Mode,
  ErrorCorrectionLevel,
  DotType,
  CornerSquareType,
  CornerDotType,
  Options,
} from "qr-code-styling";
import { usePromptStore } from "../store/promptStore";
import badger from "../components/static/badger.svg";
import logo from "../components/static/logo.svg";

const logoOptions = {
  image: logo,
  imageOptions: {
    hideBackgroundDots: true,
    imageSize: 0.2,
    margin: 6,
    crossOrigin: "anonymous",
  },
  dotsOptions: {
    type: "classy-rounded" as DotType,
  },
  cornersSquareOptions: {
    type: "extra-rounded" as CornerSquareType,
  },
  cornersDotOptions: {
    type: "extra-rounded" as CornerDotType,
  },
};

const badgerOptions = {
  image: badger,
  imageOptions: {
    hideBackgroundDots: true,
    imageSize: 0.3,
    margin: 3,
    crossOrigin: "anonymous",
  },
  dotsOptions: {
    type: "classy-rounded" as DotType,
    color: "#2D3B39", // Forrest
    gradient: {
      type: "radial",
      colorStops: [
        { offset: 0, color: "#4D6B3E" }, // Salad
        { offset: 1, color: "#2D3B39" }, // Forrest
      ],
    },
  },
  cornersSquareOptions: {
    type: "extra-rounded" as CornerSquareType,
    color: "#2D3B39", // Forrest
    gradient: {
      type: "radial",
      colorStops: [
        { offset: 0, color: "#4D6B3E" }, // Salad
        { offset: 1, color: "#2D3B39" }, // Forrest
      ],
    },
  },
  cornersDotOptions: {
    type: "extra-rounded" as CornerDotType,
    color: "#2D3B39", // Forrest
    gradient: {
      type: "radial",
      colorStops: [
        { offset: 0, color: "#4D6B3E" }, // Salad
        { offset: 1, color: "#2D3B39" }, // Forrest
      ],
    },
  },
};

export default function UserNetworkView() {
  const proxyUrl = usePromptStore((state) => state.proxyUrl);
  const [currentLogo, setCurrentLogo] = useState<Partial<Options>>(logoOptions);
  const [clickCount, setClickCount] = useState(0);
  const clickTimeout = useRef<NodeJS.Timeout>();
  const [isBadger, setIsBadger] = useState(false);

  // Reset click count after 2 seconds of no clicks
  const handleContainerClick = () => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
    }

    setClickCount((prev) => {
      const newCount = prev + 1;
      if (newCount === 3) {
        setIsBadger(!isBadger);
        setCurrentLogo(isBadger ? logoOptions : badgerOptions);
        return 0;
      }
      return newCount;
    });

    clickTimeout.current = setTimeout(() => {
      setClickCount(0);
    }, 2000);
  };

  // Log changes to the logo
  useEffect(() => {
    console.log("Current logo:", isBadger ? "Badger" : "Logo");
  }, [isBadger]);

  const [options] = useState<Options>({
    width: 400,
    height: 400,
    type: "svg" as DrawType,
    data: proxyUrl,
    margin: 15,
    qrOptions: {
      typeNumber: 0 as TypeNumber,
      mode: "Byte" as Mode,
      errorCorrectionLevel: "H" as ErrorCorrectionLevel,
    },
    backgroundOptions: {
      color: "#ffffff", // White background for better contrast
      round: 0.1,
    },
    ...currentLogo,
  });

  const [qrCode] = useState<QRCodeStyling>(new QRCodeStyling(options));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      qrCode.append(ref.current);
    }
  }, [qrCode, ref]);

  // Update QR code when logo changes
  useEffect(() => {
    if (!qrCode) return;
    qrCode.update({ ...options, ...currentLogo });
  }, [qrCode, options, currentLogo]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-full select-none">
      <h2 className="text-2xl font-semibold text-center text-[#2D3B39] select-none">
        Connect Your Device
      </h2>
      <p className="text-center mt-3 text-[#4D6B3E] select-none">
        Scan this QR code to connect your device
      </p>
      <div onClick={handleContainerClick} className="select-none">
        <div ref={ref} className="select-none" />
      </div>
    </div>
  );
}
